import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { fetchSecure } from "../utils/utils";
import VisualBuilder from "../pages/VisualBuilder";
import {
  FaPlus,
  FaSave,
  FaColumns,
  FaLayerGroup,
  FaTable,
  FaCode,
  FaMagic,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import {
  MdCheckBoxOutlineBlank,
  MdViewQuilt,
  MdViewWeek,
  MdViewStream,
} from "react-icons/md";
import { Modal, Button, Form } from "react-bootstrap";

/**
 * WYSIWYG PAGE EDITOR
 *
 * Visual editor for creating and editing pages with dynamic layouts in the SuperApp platform.
 * Allows users to build pages without writing code by selecting layouts and adding content.
 *
 * WORKFLOW:
 * 1. Initialize App - Enter App ID and click "Initialize App" (if new)
 * 2. Create Page - Enter path (URL route) and optional page title
 * 3. Select Layout - Choose from Full Screen, 50/50 Split, or 4 Quadrants
 * 4. Add Content - Click quadrants to add HTML (via ReactQuill) or SuperAppObjects
 * 5. Save Page - Persists configuration to Firestore under screens[pageKey]
 *
 * LAYOUT TYPES:
 * - Full Screen: Single content area spanning entire width
 * - 50/50 Split: Two equal-width columns side by side
 * - 4 Quadrants: Four equal regions in 2x2 grid
 *
 * CONTENT TYPES:
 * - HTML: Rich text content using ReactQuill WYSIWYG editor
 * - SuperAppObject: Dynamic data tables/forms (stored in superapp_objects collection)
 * - Workflow: MCP tools and automated workflows
 *
 * DATA STORAGE:
 * Pages are saved in SuperApp config under screens[pageKey] with structure:
 * {
 *   screens: {
 *     "pageKey": {
 *       type: "layout",
 *       title: "Page Title",
 *       structure: { type: "row", children: [...] },
 *       blocks: { "block-id": { type: "html|superappobject", ... } }
 *     }
 *   }
 * }
 *
 * When SuperAppObjects are added, their config is copied to app_config[objectKey]
 * so LowCodeBlock can find and render them.
 *
 * EDIT MODES:
 * - Dev Mode (default): Full editing capabilities, persisted in localStorage
 * - User Mode: View-only mode for end users
 * Mode can be shared via URL parameter (?mode=dev or ?mode=user)
 *
 * @see {@link ../docs/PAGE_EDITOR.md} - Complete Page Editor documentation
 * @see {@link ../docs/SUPERAPP_OBJECTS.md} - SuperAppObject creation guide
 * @see {@link ../docs/APP_ARCHITECTURE.md} - How pages are rendered in App.js
 *
 * External Resources:
 * - React Quill: https://github.com/zenoamaro/react-quill
 * - React Router: https://reactrouter.com/
 * - Firestore: https://firebase.google.com/docs/firestore
 */

const LAYOUT_TYPES = {
  FULL_SCREEN: "fullscreen",
  SPLIT_50_50: "split-50-50",
  QUADRANT: "quadrant",
  TABS: "tabs",
};

const CONTENT_TYPES = {
  HTML: "html",
  SUPERAPP_OBJECT: "superappobject",
};

export default function PageEditor() {
  console.log("🔵 PageEditor component rendering...", {
    pathname: window.location.pathname,
    search: window.location.search,
  });

  const { appId: urlAppId } = useParams();
  const history = useHistory();
  const urlAppIdParam =
    urlAppId ||
    new URLSearchParams(window.location.search).get("appId") ||
    localStorage.getItem("superAppId");
  const [appIdInput, setAppIdInput] = useState(urlAppIdParam || "");
  const appId = urlAppIdParam || appIdInput || "Ben-test";

  /* Vik (Jan 22, 2026) : Auto-populate pageKey from URL param to pre-fill fields when coming from LowCodePage empty state
   Check both 'pageKey' and 'page' params for flexibility */
  const urlPageKey =
    new URLSearchParams(window.location.search).get("pageKey") ||
    new URLSearchParams(window.location.search).get("page") ||
    "";
  const [pageKey, setPageKey] = useState(urlPageKey);
  /* Vik (Jan 22, 2026) : Auto-generate title from pageKey so user doesn't have to type it manually */
  const [pageTitle, setPageTitle] = useState(() => {
    if (!urlPageKey) return "";
    return urlPageKey
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  });
  const [layoutType, setLayoutType] = useState(null);
  const [quadrants, setQuadrants] = useState([]);
  // Tabs state
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [superAppObjects, setSuperAppObjects] = useState([]);
  const [editingQuadrant, setEditingQuadrant] = useState(null);
  const [showCreateObjectModal, setShowCreateObjectModal] = useState(false);
  const [newObjectName, setNewObjectName] = useState("");
  const [newObjectKey, setNewObjectKey] = useState("");
  const [editingSuperAppObject, setEditingSuperAppObject] = useState(null);
  const [error, setError] = useState("");
  const [showInstructions, setShowInstructions] = useState(true);
  // Load edit mode from localStorage or URL params, default to true (dev mode)
  const [isEditMode, setIsEditMode] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlMode = urlParams.get("mode");
    if (urlMode === "user") return false;
    if (urlMode === "dev") return true;
    const saved = localStorage.getItem("pageEditor_editMode");
    return saved !== null ? saved === "true" : true;
  }); // true = dev/edit mode, false = user/view mode

  // Save edit mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("pageEditor_editMode", String(isEditMode));
  }, [isEditMode]);
  const [workflows, setWorkflows] = useState([]); // Available workflows
  const [selectedWorkflow, setSelectedWorkflow] = useState(null); // Selected workflow for execution

  // Load SuperAppObjects on mount and create demo objects if needed
  useEffect(() => {
    const initialize = async () => {
      await loadSuperAppObjects();
      await loadWorkflows();
      // Small delay to ensure state is updated
      setTimeout(async () => {
        await ensureDemoObjects();
      }, 1000);
    };
    initialize();
  }, []);

  // Load available workflows
  const loadWorkflows = async () => {
    try {
      // Try to load workflows from MCP tools or a workflows collection
      const response = await fetchSecure("/api/mcp_tools");
      if (response.ok) {
        const data = await response.json();
        // Transform MCP tools into workflow format
        const workflowList = Array.isArray(data)
          ? data.map((tool, idx) => ({
              id: `workflow-${idx}`,
              name: tool.name || `Workflow ${idx + 1}`,
              description: tool.description || "",
              tool: tool,
            }))
          : [];
        setWorkflows(workflowList);
      }
    } catch (e) {
      console.warn("Failed to load workflows:", e);
      setWorkflows([]);
    }
  };

  // Helper to create demo objects if they don't exist
  const ensureDemoObjects = async () => {
    // Reload objects first to get current state
    const response = await fetchSecure("/api/superapp/objects");
    const currentObjects = response.ok ? await response.json() : [];
    const existingKeys = new Set(currentObjects.map((obj) => obj.key)); // Use Set to avoid duplicates

    // Demo 1: Users Table
    if (!existingKeys.has("demo-users")) {
      const usersDemoData = [
        {
          name: "John Doe",
          email: "john@example.com",
          role: "Admin",
          status: "Active",
        },
        {
          name: "Jane Smith",
          email: "jane@example.com",
          role: "User",
          status: "Active",
        },
        {
          name: "Bob Johnson",
          email: "bob@example.com",
          role: "Editor",
          status: "Inactive",
        },
        {
          name: "Alice Williams",
          email: "alice@example.com",
          role: "User",
          status: "Active",
        },
        {
          name: "Charlie Brown",
          email: "charlie@example.com",
          role: "Admin",
          status: "Active",
        },
      ];

      await createDemoSuperAppObject(
        "demo-users",
        "Users Table",
        {
          table: [
            {
              selector: "name",
              name: "Name",
              sortable: true,
              minWidth: "200px",
            },
            {
              selector: "email",
              name: "Email",
              sortable: true,
              minWidth: "250px",
            },
            {
              selector: "role",
              name: "Role",
              sortable: true,
              minWidth: "120px",
            },
            {
              selector: "status",
              name: "Status",
              sortable: true,
              minWidth: "100px",
            },
          ],
          fields: [
            {
              name: "name",
              display_name: "Full Name",
              input_type: "text",
              required: true,
              placeholder: "Enter full name",
            },
            {
              name: "email",
              display_name: "Email Address",
              input_type: "text",
              required: true,
              placeholder: "user@example.com",
            },
            {
              name: "role",
              display_name: "Role",
              input_type: "text",
              required: false,
              placeholder: "e.g., Admin, User",
            },
            {
              name: "status",
              display_name: "Status",
              input_type: "text",
              required: false,
              placeholder: "Active, Inactive",
            },
          ],
          collection: "demo_users",
          tableName: "demo_users",
          filterField: "name",
          defaultSort: { selector: "name", asc: true },
          otherTableOptions: { paginationPerPage: 10 },
          security: {
            view: "AllUsers",
            create: "AllUsers",
            edit: "AllUsers",
            delete: "CreatedBy",
          },
        },
        usersDemoData,
      );
    }

    // Demo 2: Products Table
    if (!existingKeys.has("demo-products")) {
      const productsDemoData = [
        {
          title: "Laptop Pro",
          category: "Electronics",
          price: "$1,299.99",
          stock: "15",
        },
        {
          title: "Wireless Mouse",
          category: "Electronics",
          price: "$29.99",
          stock: "50",
        },
        {
          title: "Office Chair",
          category: "Furniture",
          price: "$199.99",
          stock: "8",
        },
        {
          title: "Desk Lamp",
          category: "Furniture",
          price: "$49.99",
          stock: "25",
        },
        {
          title: "USB-C Cable",
          category: "Electronics",
          price: "$12.99",
          stock: "100",
        },
        {
          title: "Monitor Stand",
          category: "Furniture",
          price: "$79.99",
          stock: "12",
        },
      ];

      await createDemoSuperAppObject(
        "demo-products",
        "Products Table",
        {
          table: [
            {
              selector: "title",
              name: "Product Name",
              sortable: true,
              minWidth: "200px",
            },
            {
              selector: "category",
              name: "Category",
              sortable: true,
              minWidth: "150px",
            },
            {
              selector: "price",
              name: "Price",
              sortable: true,
              minWidth: "100px",
            },
            {
              selector: "stock",
              name: "In Stock",
              sortable: true,
              minWidth: "100px",
            },
          ],
          fields: [
            {
              name: "title",
              display_name: "Product Name",
              input_type: "text",
              required: true,
              placeholder: "Enter product name",
            },
            {
              name: "category",
              display_name: "Category",
              input_type: "text",
              required: false,
              placeholder: "e.g., Electronics, Clothing",
            },
            {
              name: "price",
              display_name: "Price",
              input_type: "text",
              required: false,
              placeholder: "0.00",
            },
            {
              name: "stock",
              display_name: "Stock Quantity",
              input_type: "text",
              required: false,
              placeholder: "0",
            },
            {
              name: "description",
              display_name: "Description",
              input_type: "text",
              required: false,
              placeholder: "Product description",
            },
          ],
          collection: "demo_products",
          tableName: "demo_products",
          filterField: "title",
          defaultSort: { selector: "title", asc: true },
          otherTableOptions: { paginationPerPage: 15 },
          security: {
            view: "AllUsers",
            create: "AllUsers",
            edit: "AllUsers",
            delete: "CreatedBy",
          },
        },
        productsDemoData,
      );
    }

    // Demo 3: Tasks Table
    if (!existingKeys.has("demo-tasks")) {
      const tasksDemoData = [
        {
          task: "Complete project documentation",
          priority: "High",
          status: "In Progress",
          dueDate: "2024-12-20",
        },
        {
          task: "Review code changes",
          priority: "Medium",
          status: "Todo",
          dueDate: "2024-12-18",
        },
        {
          task: "Update user interface",
          priority: "High",
          status: "Done",
          dueDate: "2024-12-15",
        },
        {
          task: "Fix bug in login system",
          priority: "High",
          status: "In Progress",
          dueDate: "2024-12-19",
        },
        {
          task: "Write unit tests",
          priority: "Medium",
          status: "Todo",
          dueDate: "2024-12-22",
        },
        {
          task: "Deploy to staging",
          priority: "Low",
          status: "Todo",
          dueDate: "2024-12-25",
        },
        {
          task: "Team meeting preparation",
          priority: "Medium",
          status: "Done",
          dueDate: "2024-12-17",
        },
      ];

      await createDemoSuperAppObject(
        "demo-tasks",
        "Tasks Table",
        {
          table: [
            {
              selector: "task",
              name: "Task",
              sortable: true,
              minWidth: "250px",
            },
            {
              selector: "priority",
              name: "Priority",
              sortable: true,
              minWidth: "120px",
            },
            {
              selector: "status",
              name: "Status",
              sortable: true,
              minWidth: "120px",
            },
            {
              selector: "dueDate",
              name: "Due Date",
              sortable: true,
              minWidth: "150px",
            },
          ],
          fields: [
            {
              name: "task",
              display_name: "Task Description",
              input_type: "text",
              required: true,
              placeholder: "Enter task description",
            },
            {
              name: "priority",
              display_name: "Priority",
              input_type: "text",
              required: false,
              placeholder: "High, Medium, Low",
            },
            {
              name: "status",
              display_name: "Status",
              input_type: "text",
              required: false,
              placeholder: "Todo, In Progress, Done",
            },
            {
              name: "dueDate",
              display_name: "Due Date",
              input_type: "text",
              required: false,
              placeholder: "YYYY-MM-DD",
            },
          ],
          collection: "demo_tasks",
          tableName: "demo_tasks",
          filterField: "task",
          defaultSort: { selector: "dueDate", asc: true },
          otherTableOptions: { paginationPerPage: 20 },
          security: {
            view: "AllUsers",
            create: "AllUsers",
            edit: "AllUsers",
            delete: "CreatedBy",
          },
        },
        tasksDemoData,
      );
    }

    // Reload objects after creating demos
    await loadSuperAppObjects();
  };

  // Load page config if pageKey is set (and we're editing an existing page)
  useEffect(() => {
    if (pageKey) {
      loadPageConfig();
    } else {
      // Reset state when pageKey is cleared (creating new page)
      setLayoutType(null);
      setQuadrants([]);
      setTabs([]);
      setActiveTabId(null);
      setPageTitle("");
    }
  }, [pageKey, appId]);

  // Initialize app config if it doesn't exist
  const initializeAppConfig = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetchSecure(`/api/superapp/config/${appId}`);
      if (!response.ok) {
        throw new Error(`Failed to check app config: ${response.status}`);
      }
      const data = await response.json();
      const config = data.config || data;

      // If config is empty or just has defaults, create a proper structure
      if (!config.screens || Object.keys(config.screens).length === 0) {
        const initialConfig = {
          appId: appId,
          header: { title: `${appId} App`, brand: {} },
          screens: {},
          menu: [],
          app_config: {},
        };

        const saveResponse = await fetchSecure(
          `/api/superapp/config/${appId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ config: initialConfig }),
          },
        );

        if (saveResponse.ok) {
          alert(
            `App "${appId}" initialized successfully! You can now create pages.`,
          );
          window.dispatchEvent(
            new CustomEvent("superapp-config-updated", {
              detail: { appId: appId },
            }),
          );
        } else {
          throw new Error("Failed to initialize app config");
        }
      } else {
        alert(`App "${appId}" already exists and is ready to use!`);
      }
    } catch (e) {
      console.error("Failed to initialize app config:", e);
      setError(`Failed to initialize app: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadSuperAppObjects = async () => {
    try {
      // 1. Fetch Global Objects
      const globalResponse = await fetchSecure("/api/superapp/objects");
      const globalObjects = globalResponse.ok
        ? await globalResponse.json()
        : [];

      // 2. Fetch App Config Objects (Local)
      let localObjects = [];
      if (appId) {
        try {
          const configResponse = await fetchSecure(
            `/api/superapp/config/${encodeURIComponent(appId)}`,
          );
          if (configResponse.ok) {
            const data = await configResponse.json();
            const config = data.config || data;
            if (config.app_config) {
              localObjects = Object.entries(config.app_config).map(
                ([key, value]) => ({
                  key: key,
                  displayName: value.name || key,
                  objectType: "SuperAppObject",
                  config: value,
                  isLocal: true, // Flag to identify local objects
                }),
              );
            }
          }
        } catch (err) {
          console.warn("Failed to load local app config objects:", err);
        }
      }

      // 3. Merge (Prefer Local/App-Specific if keys collide)
      const allObjectsMap = new Map();

      // Add global first
      globalObjects.forEach((obj) => allObjectsMap.set(obj.key, obj));

      // Add local (overwriting global if same key)
      localObjects.forEach((obj) => allObjectsMap.set(obj.key, obj));

      setSuperAppObjects(Array.from(allObjectsMap.values()));
    } catch (e) {
      console.error("Failed to load SuperAppObjects:", e);
    }
  };

  const loadPageConfig = async () => {
    try {
      setLoading(true);
      console.log("🔄 LOADING PAGE CONFIG:", { appId, pageKey });

      const response = await fetchSecure(`/api/superapp/config/${appId}`);
      if (response.ok) {
        const data = await response.json();
        const config = data.config || data;

        console.log("📥 FETCHED CONFIG:", {
          appId,
          availableScreens: config.screens ? Object.keys(config.screens) : [],
          targetScreen: config?.screens?.[pageKey],
        });

        const screen = config?.screens?.[pageKey];

        if (screen) {
          setPageTitle(screen.title || pageKey);

          // If screen has layout structure, extract it
          if (screen.type === "layout" && screen.structure && screen.blocks) {
            const blocks = screen.blocks;

            // Helper to extract block from a region
            const extractBlockFromRegion = (region) => {
              if (region.type === "block" && region.block) {
                return blocks[region.block];
              }
              if (region.children && Array.isArray(region.children)) {
                for (const child of region.children) {
                  const block = extractBlockFromRegion(child);
                  if (block) return block;
                }
              }
              return null;
            };

            // Extract layout type from structure
            if (screen.structure.type === "row" && screen.structure.children) {
              const children = screen.structure.children;

              // Check if it's a quadrant layout (has nested rows)
              if (
                children.length === 2 &&
                children[0].type === "row" &&
                children[1].type === "row"
              ) {
                // Quadrant layout
                setLayoutType(LAYOUT_TYPES.QUADRANT);
                const topRow = children[0].children || [];
                const bottomRow = children[1].children || [];
                const allQuadrants = [...topRow, ...bottomRow];

                const loadedQuadrants = allQuadrants
                  .slice(0, 4)
                  .map((region, idx) => {
                    const block = extractBlockFromRegion(region);
                    if (block) {
                      if (block.type === CONTENT_TYPES.HTML) {
                        return {
                          type: CONTENT_TYPES.HTML,
                          content: block.html || "",
                          blockId: region.block || `block-${pageKey}-${idx}`,
                        };
                      } else if (block.type === CONTENT_TYPES.SUPERAPP_OBJECT) {
                        return {
                          type: CONTENT_TYPES.SUPERAPP_OBJECT,
                          content: {
                            key: block.configObj || block.key,
                            objectType: "SuperAppObject",
                          },
                          blockId: region.block || `block-${pageKey}-${idx}`,
                        };
                      }
                    }
                    return {
                      type: null,
                      content: null,
                      blockId: region.block || null,
                    };
                  });
                setQuadrants(loadedQuadrants);
              } else if (children.length === 1) {
                // Full screen layout
                setLayoutType(LAYOUT_TYPES.FULL_SCREEN);
                const block = extractBlockFromRegion(children[0]);
                const quadrant = block
                  ? {
                      type:
                        block.type === "html"
                          ? CONTENT_TYPES.HTML
                          : CONTENT_TYPES.SUPERAPP_OBJECT,
                      content:
                        block.type === "html"
                          ? block.html || ""
                          : {
                              key: block.configObj || block.key,
                              objectType: "SuperAppObject",
                            },
                      blockId: children[0].block || `block-${pageKey}-0`,
                    }
                  : {
                      type: null,
                      content: null,
                      blockId: children[0]?.block || null,
                    };
                setQuadrants([quadrant]);
              } else if (children.length === 2) {
                // 50/50 split layout
                setLayoutType(LAYOUT_TYPES.SPLIT_50_50);
                const loadedQuadrants = children.map((region, idx) => {
                  const block = extractBlockFromRegion(region);
                  if (block) {
                    if (block.type === CONTENT_TYPES.HTML) {
                      return {
                        type: CONTENT_TYPES.HTML,
                        content: block.html || "",
                        blockId: region.block || `block-${pageKey}-${idx}`,
                      };
                    } else if (block.type === CONTENT_TYPES.SUPERAPP_OBJECT) {
                      return {
                        type: CONTENT_TYPES.SUPERAPP_OBJECT,
                        content: {
                          key: block.configObj || block.key,
                          objectType: "SuperAppObject",
                        },
                        blockId: region.block || `block-${pageKey}-${idx}`,
                      };
                    }
                  }
                  return {
                    type: null,
                    content: null,
                    blockId: region.block || null,
                  };
                });
                setQuadrants(loadedQuadrants);
              }
            } else if (
              screen.structure.type === "tabs" &&
              screen.structure.tabs
            ) {
              // Tabs layout
              setLayoutType(LAYOUT_TYPES.TABS);
              const loadedTabs = screen.structure.tabs.map((tab, idx) => {
                const block = extractBlockFromRegion(tab);
                const content = block
                  ? {
                      type:
                        block.type === "html"
                          ? CONTENT_TYPES.HTML
                          : CONTENT_TYPES.SUPERAPP_OBJECT,
                      content:
                        block.type === "html"
                          ? block.html || ""
                          : {
                              key: block.configObj || block.key,
                              objectType: "SuperAppObject",
                            },
                      blockId: tab.block || `block-${pageKey}-tab-${idx}`,
                    }
                  : {
                      type: null,
                      content: null,
                      blockId: tab.block || null,
                    };

                return {
                  id: tab.id || `tab-${idx}`,
                  label: tab.label || `Tab ${idx + 1}`,
                  content: content,
                };
              });
              setTabs(loadedTabs);
              if (loadedTabs.length > 0) {
                setActiveTabId(loadedTabs[0].id);
              }
            }
          }
        }
      }
    } catch (e) {
      console.error("Failed to load page config:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLayout = (type) => {
    setLayoutType(type);

    if (type === LAYOUT_TYPES.TABS) {
      setTabs([
        {
          id: "tab-1",
          label: "Tab 1",
          content: { type: null, content: null, blockId: null },
        },
        {
          id: "tab-2",
          label: "Tab 2",
          content: { type: null, content: null, blockId: null },
        },
      ]);
      setActiveTabId("tab-1");
      setQuadrants([]); // Clear quadrants
    } else {
      let numQuadrants = 1;
      if (type === LAYOUT_TYPES.SPLIT_50_50) numQuadrants = 2;
      else if (type === LAYOUT_TYPES.QUADRANT) numQuadrants = 4;

      setQuadrants(
        Array(numQuadrants)
          .fill(null)
          .map(() => ({
            type: null,
            content: null,
            blockId: null,
          })),
      );
      setTabs([]); // Clear tabs
    }
  };

  const handleQuadrantClick = (index) => {
    // If quadrant has a SuperAppObject, allow editing it directly
    const target =
      layoutType === LAYOUT_TYPES.TABS
        ? tabs.find((t) => t.id === activeTabId)?.content
        : quadrants[index];

    if (
      target?.type === CONTENT_TYPES.SUPERAPP_OBJECT &&
      target?.content?.key
    ) {
      const objectKey = target.content.key;
      const superAppObject = superAppObjects.find(
        (obj) => obj.key === objectKey,
      );
      if (superAppObject) {
        setEditingSuperAppObject(superAppObject);
        return;
      }
    }
    setEditingQuadrant(index); // For tabs, index 0 is the active tab content
  };

  // Vik (Jan 19, 2026) : Modified to accept closeEditor flag. This allows us to save content (like HTML)
  // without closing the editor modal, which is essential when switching between the basic editor and Visual Builder.
  const handleSetQuadrantContent = (
    index,
    contentType,
    content,
    closeEditor = true,
  ) => {
    if (layoutType === LAYOUT_TYPES.TABS) {
      const newTabs = tabs.map((tab) => {
        if (tab.id === activeTabId) {
          return {
            ...tab,
            content: {
              type: contentType,
              content: content,
              blockId: `block-${pageKey}-tab-${tab.id}-${Date.now()}`,
            },
          };
        }
        return tab;
      });
      setTabs(newTabs);
    } else {
      const newQuadrants = [...quadrants];
      newQuadrants[index] = {
        type: contentType,
        content: content,
        blockId: `block-${pageKey}-${index}-${Date.now()}`,
      };
      setQuadrants(newQuadrants);
    }
    if (closeEditor) {
      setEditingQuadrant(null);
    }
  };

  // Tab management functions
  const handleAddTab = () => {
    const newId = `tab-${Date.now()}`;
    setTabs([
      ...tabs,
      {
        id: newId,
        label: `Tab ${tabs.length + 1}`,
        content: { type: null, content: null, blockId: null },
      },
    ]);
    setActiveTabId(newId);
  };

  const handleDeleteTab = (tabId, e) => {
    e.stopPropagation();
    if (tabs.length <= 1) {
      alert("You must have at least one tab.");
      return;
    }
    const newTabs = tabs.filter((t) => t.id !== tabId);
    setTabs(newTabs);
    if (activeTabId === tabId) {
      setActiveTabId(newTabs[0].id);
    }
  };

  const handleRenameTab = (tabId, newLabel) => {
    setTabs(tabs.map((t) => (t.id === tabId ? { ...t, label: newLabel } : t)));
  };

  // Helper function to create demo data in a collection (only if collection is empty)
  const createDemoData = async (collection) => {
    try {
      console.log(`🔵 Populating demo data for ${collection}...`);

      // Use the new endpoint that doesn't require authentication
      const response = await fetch("/api/populate-demo-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ collection }),
      });

      // Check content type first
      const contentType = response.headers.get("content-type") || "";
      const isJSON = contentType.includes("application/json");

      console.log(`🔵 Response for ${collection}:`, {
        status: response.status,
        statusText: response.statusText,
        contentType: contentType,
        ok: response.ok,
        url: response.url,
      });

      if (response.ok && isJSON) {
        const result = await response.json();
        if (result.skipped) {
          console.log(`⏭️ ${result.message}`);
          return { success: true, skipped: true, ...result };
        } else {
          console.log(`✅ ${result.message}`);
          return { success: true, ...result };
        }
      } else {
        // Response is not JSON (likely HTML redirect or error page)
        const errorText = await response.text().catch(() => "Unknown error");
        const isHTML =
          errorText.trim().startsWith("<!DOCTYPE") ||
          errorText.trim().startsWith("<html");

        if (isHTML) {
          console.error(
            `❌ Got HTML response for ${collection} (likely redirected). Status: ${response.status}`,
          );
          console.error(`Response preview:`, errorText.substring(0, 200));
          return {
            success: false,
            error: `Server returned HTML instead of JSON. The endpoint may not be registered or is being blocked by middleware. Status: ${response.status}`,
          };
        } else {
          let error;
          try {
            error = JSON.parse(errorText);
          } catch {
            error = { error: errorText || "Unknown error" };
          }
          console.error(
            `❌ Failed to populate ${collection}:`,
            error.error || error.message || "Unknown error",
          );
          console.error(`Response status: ${response.status}`, error);
          return {
            success: false,
            error: error.error || error.message || "Unknown error",
          };
        }
      }
    } catch (e) {
      console.error(`❌ Failed to populate demo data in ${collection}:`, e);
      console.error("Error details:", e.message, e.stack);
      return { success: false, error: e.message || "Network error" };
    }
  };

  // Helper function to create demo SuperAppObjects with example configs
  const createDemoSuperAppObject = async (
    key,
    displayName,
    config,
    demoData = [],
  ) => {
    try {
      const response = await fetchSecure("/api/superapp/objects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          key: key,
          displayName: displayName,
          objectType: "SuperAppObject",
          config: config,
        }),
      });

      if (response.ok) {
        const newObject = await response.json();
        setSuperAppObjects([...superAppObjects, newObject]);

        // Create demo data if provided
        if (demoData.length > 0 && config.collection) {
          await createDemoData(config.collection);
        }

        return newObject;
      } else {
        const error = await response.json();
        console.error(`Failed to create ${key}:`, error);
        return null;
      }
    } catch (e) {
      console.error(`Failed to create ${key}:`, e);
      return null;
    }
  };

  const handleCreateNewObject = async () => {
    if (!newObjectName || !newObjectKey) {
      alert("Please enter both name and key");
      return;
    }

    try {
      const response = await fetchSecure("/api/superapp/objects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          key: newObjectKey,
          displayName: newObjectName,
          objectType: "SuperAppObject",
          config: {},
        }),
      });

      if (response.ok) {
        const newObject = await response.json();
        setSuperAppObjects([...superAppObjects, newObject]);
        setShowCreateObjectModal(false);
        setNewObjectName("");
        setNewObjectKey("");
        // Auto-select the new object
        if (editingQuadrant !== null) {
          handleSetQuadrantContent(
            editingQuadrant,
            CONTENT_TYPES.SUPERAPP_OBJECT,
            { key: newObjectKey, objectType: "SuperAppObject" },
          );
        }
      } else {
        const error = await response.json();
        alert(`Failed to create object: ${error.error || "Unknown error"}`);
      }
    } catch (e) {
      console.error("Failed to create SuperAppObject:", e);
      alert("Failed to create SuperAppObject");
    }
  };

  const handleSavePage = async () => {
    if (!pageKey) {
      setError("Please enter a path (URL route like 'info' or 'about')");
      return;
    }

    if (!layoutType) {
      setError("Please select a layout first");
      return;
    }

    if (!appId || appId.trim() === "") {
      setError("Please enter an App ID");
      return;
    }

    try {
      setSaving(true);
      setError("");

      // Load current config (or create default if it doesn't exist)
      const configResponse = await fetchSecure(`/api/superapp/config/${appId}`);
      if (!configResponse.ok) {
        throw new Error(
          `Failed to load app config: ${configResponse.status} ${configResponse.statusText}`,
        );
      }
      const configData = await configResponse.json();
      const currentConfig = configData.config || configData || {};

      // Ensure app_config exists
      if (!currentConfig.app_config) {
        currentConfig.app_config = {};
      }

      // Build layout structure
      let structure = null;
      const blocks = {};

      // First, copy SuperAppObject configs into app_config if they're from superapp_objects collection
      const itemsToProcess =
        layoutType === LAYOUT_TYPES.TABS
          ? tabs.map((t) => t.content)
          : quadrants;

      for (const item of itemsToProcess) {
        if (item?.type === CONTENT_TYPES.SUPERAPP_OBJECT && item.content?.key) {
          const objectKey = item.content.key;
          // Check if this SuperAppObject exists in superapp_objects collection
          const superAppObject = superAppObjects.find(
            (obj) => obj.key === objectKey,
          );
          if (superAppObject && superAppObject.config) {
            // Copy the SuperAppObject config into app_config so LowCodeBlock can find it
            currentConfig.app_config[objectKey] = {
              ...superAppObject.config,
              name: superAppObject.displayName,
              tableName: superAppObject.config.tableName || objectKey,
              collection: superAppObject.config.collection || objectKey,
            };
          }
        }
      }

      if (layoutType === LAYOUT_TYPES.FULL_SCREEN) {
        // Always create structure, even if empty (to clear old layouts)
        if (quadrants[0]?.blockId) {
          structure = {
            type: "row",
            id: `row-${pageKey}`,
            children: [
              {
                type: "column",
                id: `col-${pageKey}-0`,
                children: [
                  {
                    type: "block",
                    id: quadrants[0].blockId,
                    block: quadrants[0].blockId,
                  },
                ],
              },
            ],
          };

          if (quadrants[0].type === CONTENT_TYPES.HTML) {
            blocks[quadrants[0].blockId] = {
              type: "html",
              html:
                typeof quadrants[0].content === "object"
                  ? quadrants[0].content.html
                  : quadrants[0].content,
            };
          } else if (quadrants[0].type === CONTENT_TYPES.SUPERAPP_OBJECT) {
            blocks[quadrants[0].blockId] = {
              type: "superappobject",
              configObj: quadrants[0].content.key,
              ...quadrants[0].content,
            };
          } else if (quadrants[0].type === CONTENT_TYPES.WORKFLOW) {
            blocks[quadrants[0].blockId] = {
              type: "workflow",
              workflowId: quadrants[0].content.workflowId,
              workflowName: quadrants[0].content.workflowName,
              ...quadrants[0].content,
            };
          }
        } else {
          // Empty full screen layout - create structure with empty column
          structure = {
            type: "row",
            id: `row-${pageKey}`,
            children: [
              {
                type: "column",
                id: `col-${pageKey}-0`,
                children: [],
              },
            ],
          };
        }
      } else if (layoutType === LAYOUT_TYPES.SPLIT_50_50) {
        const children = quadrants.map((q, idx) => {
          if (q?.blockId) {
            if (q.type === CONTENT_TYPES.HTML) {
              blocks[q.blockId] = {
                type: "html",
                html:
                  typeof q.content === "object" ? q.content.html : q.content,
              };
            } else if (q.type === CONTENT_TYPES.SUPERAPP_OBJECT) {
              blocks[q.blockId] = {
                type: "superappobject",
                configObj: q.content.key,
                ...q.content,
              };
            } else if (q.type === CONTENT_TYPES.WORKFLOW) {
              blocks[q.blockId] = {
                type: "workflow",
                workflowId: q.content.workflowId,
                workflowName: q.content.workflowName,
                ...q.content,
              };
            }

            return {
              type: "column",
              id: `col-${pageKey}-${idx}`,
              sectionProps: { style: { flex: 1 } },
              children: [
                {
                  type: "block",
                  id: q.blockId,
                  block: q.blockId,
                },
              ],
            };
          }
          // Return empty column even if no content (to maintain layout structure)
          return {
            type: "column",
            id: `col-${pageKey}-${idx}`,
            sectionProps: { style: { flex: 1 } },
            children: [],
          };
        });

        structure = {
          type: "row",
          id: `row-${pageKey}`,
          sectionProps: { style: { display: "flex", gap: "16px" } },
          children: children,
        };
      } else if (layoutType === LAYOUT_TYPES.QUADRANT) {
        const topRow = quadrants.slice(0, 2).map((q, idx) => {
          if (q?.blockId) {
            if (q.type === CONTENT_TYPES.HTML) {
              blocks[q.blockId] = {
                type: "html",
                html:
                  typeof q.content === "object" ? q.content.html : q.content,
              };
            } else if (q.type === CONTENT_TYPES.SUPERAPP_OBJECT) {
              blocks[q.blockId] = {
                type: "superappobject",
                configObj: q.content.key,
                ...q.content,
              };
            } else if (q.type === CONTENT_TYPES.WORKFLOW) {
              blocks[q.blockId] = {
                type: "workflow",
                workflowId: q.content.workflowId,
                workflowName: q.content.workflowName,
                ...q.content,
              };
            }

            return {
              type: "column",
              id: `col-${pageKey}-${idx}`,
              sectionProps: { style: { flex: 1 } },
              children: [
                {
                  type: "block",
                  id: q.blockId,
                  block: q.blockId,
                },
              ],
            };
          }
          // Return empty column even if no content (to maintain layout structure)
          return {
            type: "column",
            id: `col-${pageKey}-${idx}`,
            sectionProps: { style: { flex: 1 } },
            children: [],
          };
        });

        const bottomRow = quadrants.slice(2, 4).map((q, idx) => {
          if (q?.blockId) {
            if (q.type === CONTENT_TYPES.HTML) {
              blocks[q.blockId] = {
                type: "html",
                html:
                  typeof q.content === "object" ? q.content.html : q.content,
              };
            } else if (q.type === CONTENT_TYPES.SUPERAPP_OBJECT) {
              blocks[q.blockId] = {
                type: "superappobject",
                configObj: q.content.key,
                ...q.content,
              };
            } else if (q.type === CONTENT_TYPES.WORKFLOW) {
              blocks[q.blockId] = {
                type: "workflow",
                workflowId: q.content.workflowId,
                workflowName: q.content.workflowName,
                ...q.content,
              };
            }

            return {
              type: "column",
              id: `col-${pageKey}-${idx + 2}`,
              sectionProps: { style: { flex: 1 } },
              children: [
                {
                  type: "block",
                  id: q.blockId,
                  block: q.blockId,
                },
              ],
            };
          }
          // Return empty column even if no content (to maintain layout structure)
          return {
            type: "column",
            id: `col-${pageKey}-${idx + 2}`,
            sectionProps: { style: { flex: 1 } },
            children: [],
          };
        });

        structure = {
          type: "row",
          id: `row-${pageKey}-container`,
          sectionProps: {
            style: { display: "flex", flexDirection: "column", gap: "16px" },
          },
          children: [
            {
              type: "row",
              id: `row-${pageKey}-top`,
              sectionProps: { style: { display: "flex", gap: "16px" } },
              children: topRow,
            },
            {
              type: "row",
              id: `row-${pageKey}-bottom`,
              sectionProps: { style: { display: "flex", gap: "16px" } },
              children: bottomRow,
            },
          ],
        };
      } else if (layoutType === LAYOUT_TYPES.TABS) {
        const tabChildren = tabs.map((tab, idx) => {
          // Ensure content exists before accessing properties
          if (tab.content && tab.content.type) {
            const blockId =
              tab.content.blockId ||
              `block-${pageKey}-tab-${idx}-${Date.now()}`;

            // Add block definition
            if (tab.content.type === CONTENT_TYPES.HTML) {
              blocks[blockId] = {
                type: "html",
                html:
                  typeof tab.content.content === "object"
                    ? tab.content.content.html
                    : tab.content.content,
              };
            } else if (tab.content.type === CONTENT_TYPES.SUPERAPP_OBJECT) {
              blocks[blockId] = {
                type: "superappobject",
                configObj: tab.content.content.key,
              };
            }

            return {
              type: "tab",
              id: tab.id,
              label: tab.label,
              block: blockId,
            };
          }

          // Return tab without block if no content
          return {
            type: "tab",
            id: tab.id,
            label: tab.label,
            block: null,
          };
        });

        structure = {
          type: "tabs",
          id: `tabs-${pageKey}`,
          tabs: tabChildren,
        };
      }

      // Update screens config
      const updatedConfig = {
        ...currentConfig,
        screens: {
          ...(currentConfig.screens || {}),
          [pageKey]: {
            type: "layout",
            title: pageTitle || pageKey,
            structure: structure,
            blocks: blocks,
          },
        },
      };

      // Vik (Jan 15, 2026): Auto-add to menu if not present
      const menuKey = pageKey.toLowerCase();
      const menuLabel = pageTitle || pageKey;
      const menuPath = `/${pageKey}`;

      // Helper to check if item exists in a list
      const existsIn = (list) =>
        list?.some(
          (item) =>
            (item.key && item.key.toLowerCase() === menuKey) ||
            (item.path && item.path.toLowerCase() === menuPath),
        );

      let menuUpdated = false;

      // Vik (Jan 15, 2026) : Try header.primaryMenu.items first (preferred), BUT only if it already has items
      // or if legacy menu is empty. This prevents overriding legacy menu with a single new item.
      const primaryItems = updatedConfig.header?.primaryMenu?.items;
      const legacyItems = updatedConfig.menu;

      const hasPrimary = Array.isArray(primaryItems) && primaryItems.length > 0;
      const hasLegacy = Array.isArray(legacyItems) && legacyItems.length > 0;

      console.log("Vik (Jan 16, 2026) : PageEditor Menu Update Check", {
        hasPrimary,
        hasLegacy,
        primaryItems,
        legacyItems,
      });

      if (hasPrimary) {
        if (!existsIn(updatedConfig.header.primaryMenu.items)) {
          // Vik (Jan 15, 2026) : Adding to existing Primary Menu
          updatedConfig.header.primaryMenu.items.push({
            key: menuKey,
            label: menuLabel,
            path: menuPath,
            visible: true,
            order: updatedConfig.header.primaryMenu.items.length,
          });
          menuUpdated = true;
        }
      }
      // Fallback to legacy menu if it has items (maintain legacy mode)
      else if (hasLegacy) {
        if (!existsIn(updatedConfig.menu)) {
          // Vik (Jan 15, 2026) : Adding to existing Legacy Menu (to avoid hiding other legacy items)
          updatedConfig.menu.push({
            key: menuKey,
            label: menuLabel,
            path: menuPath,
            visible: true,
            order: updatedConfig.menu.length,
          });
          menuUpdated = true;
        }
      }
      // If neither exists, create header.primaryMenu.items (start modern mode)
      else {
        if (!updatedConfig.header) updatedConfig.header = {};
        if (!updatedConfig.header.primaryMenu)
          updatedConfig.header.primaryMenu = {};

        // Vik (Jan 15, 2026) : Initializing new Primary Menu structure
        updatedConfig.header.primaryMenu.items = [
          {
            key: menuKey,
            label: menuLabel,
            path: menuPath,
            visible: true,
            order: 0,
          },
        ];
        menuUpdated = true;
      }

      if (menuUpdated) {
        console.log("➕ Auto-added page to menu:", {
          key: menuKey,
          label: menuLabel,
        });
      }

      console.log("💾 SAVING PAGE:", {
        appId,
        pageKey,
        structureType: structure.type,
        tabsCount: structure.tabs ? structure.tabs.length : 0,
        fullConfig: updatedConfig,
      });

      // Save config (API expects { config: ... } format)
      const saveResponse = await fetchSecure(`/api/superapp/config/${appId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ config: updatedConfig }),
      });

      if (saveResponse.ok) {
        setError("");
        alert("Page saved successfully!");
        // Trigger config refresh event so LowCodePage picks up the changes
        window.dispatchEvent(
          new CustomEvent("superapp-config-updated", {
            detail: { appId: appId },
          }),
        );
        // Redirect to the app home page
        history.push(`/?appId=${appId}`);
      } else {
        const errorData = await saveResponse
          .json()
          .catch(() => ({ error: "Unknown error" }));
        const errorMsg =
          errorData.error || `Failed to save page (${saveResponse.status})`;
        setError(errorMsg);
        alert(`Failed to save page: ${errorMsg}`);
      }
    } catch (e) {
      console.error("Failed to save page:", e);
      const errorMsg =
        e.message ||
        "Failed to save page. Please check the console for details.";
      setError(errorMsg);
      alert(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Toggle component - always visible in both modes
  /**
   * Dev/User Mode Toggle Component
   *
   * Toggle between Dev mode (editing enabled) and User mode (view-only).
   *
   * IMPORTANT: This component is always rendered (not conditionally) to ensure
   * users can always switch modes, even when in User mode. Previously, the toggle
   * was hidden in User mode, preventing users from switching back to Dev mode.
   *
   * The mode is persisted to:
   * - localStorage: 'pageEditor_editMode' for session persistence
   * - URL search params: 'mode=dev' or 'mode=user' for shareable URLs
   */
  const ModeToggle = () => (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <span style={{ color: "#fff", fontSize: "14px", fontWeight: "bold" }}>
        User
      </span>
      <label
        style={{
          position: "relative",
          display: "inline-block",
          width: "60px",
          height: "30px",
        }}
      >
        <input
          type="checkbox"
          checked={isEditMode}
          onChange={(e) => {
            const newMode = e.target.checked;
            setIsEditMode(newMode);
            // Persist mode to localStorage and URL params
            // This allows the mode to survive page refreshes and be shareable via URL
            localStorage.setItem("pageEditor_editMode", newMode.toString());
            const url = new URL(window.location);
            url.searchParams.set("mode", newMode ? "dev" : "user");
            window.history.replaceState({}, "", url);
            // Note: No page refresh needed - component re-renders and toggles edit/view UI accordingly
          }}
          style={{ opacity: 0, width: 0, height: 0 }}
        />
        <span
          style={{
            position: "absolute",
            cursor: "pointer",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isEditMode ? "#4caf50" : "#ccc",
            transition: "0.3s",
            borderRadius: "30px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
        >
          <span
            style={{
              position: "absolute",
              content: '""',
              height: "24px",
              width: "24px",
              left: isEditMode ? "32px" : "3px",
              bottom: "3px",
              backgroundColor: "#fff",
              transition: "0.3s",
              borderRadius: "50%",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
          />
        </span>
      </label>
      <span style={{ color: "#fff", fontSize: "14px", fontWeight: "bold" }}>
        Dev
      </span>
      <span
        style={{
          color: isEditMode ? "#4caf50" : "#9ba5c0",
          fontSize: "12px",
          padding: "4px 8px",
          background: isEditMode
            ? "rgba(76, 175, 80, 0.2)"
            : "rgba(155, 165, 192, 0.2)",
          borderRadius: "4px",
        }}
      >
        {isEditMode ? "✏️ Edit Mode" : "👁️ View Mode"}
      </span>
    </div>
  );

  const renderLayoutPreview = (editMode = true) => {
    if (!layoutType) {
      // Vik (Jan 22, 2026) : Simplified placeholder - removed duplicate colorful layout buttons (Layout Structure card above handles selection)
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            padding: "40px",
            background: "rgba(255,255,255,0.02)",
            borderRadius: "12px",
            border: "1px dashed rgba(255,255,255,0.1)",
          }}
        >
          <p
            style={{
              color: "rgba(255,255,255,0.5)",
              margin: 0,
              textAlign: "center",
            }}
          >
            👆 Select a layout from the options above to start building your
            page
          </p>
        </div>
      );
    }

    if (layoutType === LAYOUT_TYPES.TABS) {
      return (
        <div className="tabs-editor" style={{ marginTop: 20 }}>
          {/* Tabs Header */}
          <div
            style={{
              display: "flex",
              gap: 2,
              borderBottom: "1px solid rgba(255,255,255,0.2)",
              marginBottom: 20,
              overflowX: "auto",
            }}
          >
            {tabs.map((tab) => (
              <div
                key={tab.id}
                onClick={() => setActiveTabId(tab.id)}
                style={{
                  padding: "8px 16px",
                  cursor: "pointer",
                  background:
                    activeTabId === tab.id
                      ? "rgba(255,255,255,0.1)"
                      : "transparent",
                  borderBottom:
                    activeTabId === tab.id ? "2px solid #007bff" : "none",
                  color: activeTabId === tab.id ? "#fff" : "#aaa",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  minWidth: 100,
                }}
              >
                <input
                  type="text"
                  value={tab.label}
                  onChange={(e) => handleRenameTab(tab.id, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "inherit",
                    width: "100%",
                    outline: "none",
                  }}
                />
                <span
                  onClick={(e) => handleDeleteTab(tab.id, e)}
                  style={{ opacity: 0.5, cursor: "pointer", fontSize: "1.2em" }}
                >
                  &times;
                </span>
              </div>
            ))}
            <div
              onClick={handleAddTab}
              style={{
                padding: "8px 16px",
                cursor: "pointer",
                color: "#007bff",
                display: "flex",
                alignItems: "center",
              }}
            >
              + Add Tab
            </div>
          </div>

          {/* Active Tab Content */}
          {tabs.find((t) => t.id === activeTabId) && (
            <div style={{ minHeight: 400 }}>
              {(() => {
                const activeTab = tabs.find((t) => t.id === activeTabId);
                const content = activeTab.content;

                if (!content.type) {
                  return (
                    <div
                      onClick={() => setEditingQuadrant(0)} // 0 is just a placeholder index for tabs
                      style={{
                        height: 400,
                        border: "2px dashed rgba(255,255,255,0.2)",
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        color: "#aaa",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <span style={{ fontSize: "2em" }}>+</span>
                      <span>Add Content to {activeTab.label}</span>
                    </div>
                  );
                }

                return (
                  <div style={{ position: "relative", height: "100%" }}>
                    <div
                      style={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        zIndex: 10,
                      }}
                    >
                      <button
                        onClick={() => setEditingQuadrant(0)}
                        style={{
                          background: "rgba(0,0,0,0.5)",
                          color: "#fff",
                          border: "none",
                          padding: "5px 10px",
                          borderRadius: 4,
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleSetQuadrantContent(0, null, null)} // Clear content
                        style={{
                          background: "rgba(255,0,0,0.5)",
                          color: "#fff",
                          border: "none",
                          padding: "5px 10px",
                          borderRadius: 4,
                          cursor: "pointer",
                          marginLeft: 5,
                        }}
                      >
                        Clear
                      </button>
                    </div>

                    {content.type === CONTENT_TYPES.HTML ? (
                      <div
                        className="ql-editor"
                        style={{
                          background: "#fff",
                          color: "#000",
                          padding: 20,
                          borderRadius: 8,
                          minHeight: 400,
                          // Vik (Jan 16, 2026) : Apply container styles if available
                          ...(typeof content.content === "object" &&
                          content.content.style
                            ? content.content.style
                            : {}),
                        }}
                        dangerouslySetInnerHTML={{
                          __html:
                            typeof content.content === "string"
                              ? content.content
                              : content.content.html,
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          background: "rgba(255,255,255,0.05)",
                          padding: 20,
                          borderRadius: 8,
                          minHeight: 400,
                        }}
                      >
                        <h4 style={{ color: "#fff" }}>
                          SuperApp Object: {content.content.key}
                        </h4>
                        <p style={{ color: "#aaa" }}>
                          This object will be rendered here.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      );
    }

    if (layoutType === LAYOUT_TYPES.FULL_SCREEN) {
      return (
        <div
          style={{
            display: "flex",
            minHeight: "400px",
            border: "2px solid rgba(255,255,255,0.3)",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <Quadrant
            index={0}
            quadrant={quadrants[0]}
            onClick={() => handleQuadrantClick(0)}
            style={{ flex: 1 }}
            isEditMode={editMode}
          />
        </div>
      );
    }

    if (layoutType === LAYOUT_TYPES.SPLIT_50_50) {
      return (
        <div
          style={{
            display: "flex",
            gap: "16px",
            minHeight: "400px",
          }}
        >
          <Quadrant
            index={0}
            quadrant={quadrants[0]}
            onClick={() => handleQuadrantClick(0)}
            style={{ flex: 1 }}
            isEditMode={editMode}
          />
          <Quadrant
            index={1}
            quadrant={quadrants[1]}
            onClick={() => handleQuadrantClick(1)}
            style={{ flex: 1 }}
            isEditMode={editMode}
          />
        </div>
      );
    }

    if (layoutType === LAYOUT_TYPES.QUADRANT) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            minHeight: "400px",
          }}
        >
          <div style={{ display: "flex", gap: "16px", flex: 1 }}>
            <Quadrant
              index={0}
              quadrant={quadrants[0]}
              onClick={() => handleQuadrantClick(0)}
              style={{ flex: 1 }}
              isEditMode={editMode}
            />
            <Quadrant
              index={1}
              quadrant={quadrants[1]}
              onClick={() => handleQuadrantClick(1)}
              style={{ flex: 1 }}
              isEditMode={editMode}
            />
          </div>
          <div style={{ display: "flex", gap: "16px", flex: 1 }}>
            <Quadrant
              index={2}
              quadrant={quadrants[2]}
              onClick={() => handleQuadrantClick(2)}
              style={{ flex: 1 }}
              isEditMode={editMode}
            />
            <Quadrant
              index={3}
              quadrant={quadrants[3]}
              onClick={() => handleQuadrantClick(3)}
              style={{ flex: 1 }}
              isEditMode={editMode}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  // If in user mode, show blank page with toggle
  if (!isEditMode) {
    return (
      <div
        style={{
          padding: "24px",
          color: "#fff",
          maxWidth: "1400px",
          margin: "0 auto",
          minHeight: "400px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <h1 style={{ color: "#fff", margin: 0 }}>Page Editor</h1>
          <ModeToggle />
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <h2 style={{ color: "#fff", marginBottom: "16px" }}>View Mode</h2>
          <p style={{ color: "#9ba5c0" }}>Switch to Dev mode to edit pages</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-editor-container">
      <div className="page-editor-header">
        <div className="custom-container">
          <h1 className="gradient-text">Page Editor</h1>
          <ModeToggle />
        </div>
      </div>

      <div className="custom-container">
        {/* Vik (Jan 22, 2026) : Simplified App Configuration to badge - user is already inside app, no need for Initialize button */}
        <div className="app-context-badge mb-4">
          <FaLayerGroup className="icon" />
          <span>
            Editing App: <strong>{appId}</strong>
          </span>
        </div>

        {/* Instructions Collapsible */}
        {showInstructions && (
          <div className="info-alert mb-4">
            <div className="alert-header">
              <h3>
                <FaMagic className="icon" /> Quick Start Guide
              </h3>
              <button
                onClick={() => setShowInstructions(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <ol className="alert-list">
              <li>
                <strong>Path:</strong> Enter or verify the URL path for your
                page.
              </li>
              <li>
                <strong>Layout:</strong> Choose a structure for your content.
              </li>
              <li>
                <strong>Content:</strong> Click quadrants to add HTML or Data
                Tables.
              </li>
              <li>
                <strong>Save:</strong> Persist your changes to the database.
              </li>
            </ol>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="error-alert mb-4">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Demo Data Button */}
        {superAppObjects.some((obj) =>
          ["demo-users", "demo-products", "demo-tasks"].includes(obj.key),
        ) && (
          <div className="mb-4 text-end">
            <button
              onClick={async () => {
                if (
                  window.confirm(
                    "This will populate demo data for Users, Products, and Tasks tables. Continue?",
                  )
                ) {
                  const results = await Promise.all([
                    createDemoData("demo_users"),
                    createDemoData("demo_products"),
                    createDemoData("demo_tasks"),
                  ]);

                  const successCount = results.filter(
                    (r) => r?.success && !r?.skipped,
                  ).length;
                  const skippedCount = results.filter((r) => r?.skipped).length;
                  const failedCount = results.filter((r) => !r?.success).length;

                  if (successCount > 0 || skippedCount > 0) {
                    const message = `Demo data populated! ${successCount} collections created, ${skippedCount} already had data. Refresh the /info page to see it.`;
                    console.log("✅", message);
                    window.alert(message);
                  } else if (failedCount > 0) {
                    const errors = results
                      .filter((r) => !r?.success)
                      .map((r) => r?.error || "Unknown error")
                      .join(", ");
                    const message = `Failed to populate demo data. Errors: ${errors}. Check the console for details.`;
                    console.error("❌", message);
                    window.alert(message);
                  }
                }
              }}
              className="secondary-btn"
            >
              <FaTable className="icon" /> Populate Demo Data
            </button>
          </div>
        )}

        {/* Page Configuration */}
        <div className="editor-card mb-4">
          <div className="card-header">
            <h3 className="card-title">Page Configuration</h3>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Path / URL</label>
                <div className="input-wrapper">
                  <span className="input-prefix">/</span>
                  <input
                    type="text"
                    placeholder="e.g. about"
                    value={pageKey}
                    onChange={(e) => {
                      let value = e.target.value;
                      value = value
                        .replace(/^\//, "")
                        .toLowerCase()
                        .replace(/[^a-z0-9-]/g, "");
                      setPageKey(value);
                    }}
                    className="premium-input"
                  />
                </div>
                <p className="helper-text">
                  The URL where this page will live.
                </p>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Page Title</label>
                <input
                  type="text"
                  placeholder="e.g. About Us"
                  value={pageTitle}
                  onChange={(e) => setPageTitle(e.target.value)}
                  className="premium-input"
                />
                <p className="helper-text">
                  Displayed in browser tab and menus.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Layout Selection */}
        {isEditMode && (
          <div className="editor-card mb-4">
            <div className="card-header">
              <h3 className="card-title">Layout Structure</h3>
            </div>
            <div className="card-body">
              <div className="layout-grid">
                <div
                  className={`layout-option ${
                    layoutType === LAYOUT_TYPES.FULL_SCREEN ? "selected" : ""
                  }`}
                  onClick={() => handleSelectLayout(LAYOUT_TYPES.FULL_SCREEN)}
                >
                  <div className="layout-icon">
                    <MdCheckBoxOutlineBlank />
                  </div>
                  <span>Full Screen</span>
                </div>
                <div
                  className={`layout-option ${
                    layoutType === LAYOUT_TYPES.SPLIT_50_50 ? "selected" : ""
                  }`}
                  onClick={() => handleSelectLayout(LAYOUT_TYPES.SPLIT_50_50)}
                >
                  <div className="layout-icon">
                    <FaColumns />
                  </div>
                  <span>50/50 Split</span>
                </div>
                <div
                  className={`layout-option ${
                    layoutType === LAYOUT_TYPES.QUADRANT ? "selected" : ""
                  }`}
                  onClick={() => handleSelectLayout(LAYOUT_TYPES.QUADRANT)}
                >
                  <div className="layout-icon">
                    <MdViewQuilt />
                  </div>
                  <span>4 Quadrants</span>
                </div>
                <div
                  className={`layout-option ${
                    layoutType === LAYOUT_TYPES.TABS ? "selected" : ""
                  }`}
                  onClick={() => handleSelectLayout(LAYOUT_TYPES.TABS)}
                >
                  <div className="layout-icon">
                    <MdViewStream />
                  </div>
                  <span>Tabs</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isEditMode && (
          <div className="view-mode-banner">
            <p>
              👁️ <strong>View Mode Active</strong> - Switch to Dev mode to edit
              layouts and content.
            </p>
          </div>
        )}

        {/* Editor Workspace */}
        {renderLayoutPreview(isEditMode)}

        {/* Vik (Jan 23, 2026) : Floating Save Button at bottom of page for better UX */}
        {isEditMode && (
          <div className="floating-save-container">
            <div className="floating-save-inner">
              <div className="save-status">
                {pageKey && layoutType ? (
                  <span className="status-ready">
                    <FaSave className="status-icon" /> Ready to save
                  </span>
                ) : (
                  <span className="status-pending">
                    {!pageKey ? "Enter a path" : "Select a layout"} to continue
                  </span>
                )}
              </div>
              <button
                onClick={handleSavePage}
                disabled={saving || !pageKey || !layoutType}
                className={`floating-save-btn ${saving || !pageKey || !layoutType ? "disabled" : ""}`}
              >
                {saving ? (
                  <>
                    <span className="spinner"></span> Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="icon" /> Save Page
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Modals */}
        {/* Visual Builder for HTML Content */}
        {editingQuadrant !== null &&
          isEditMode &&
          (layoutType === LAYOUT_TYPES.TABS
            ? tabs.find((t) => t.id === activeTabId)?.content?.type ===
              CONTENT_TYPES.HTML
            : quadrants[editingQuadrant]?.type === CONTENT_TYPES.HTML) && (
            <VisualBuilder
              initialContent={(() => {
                const q =
                  layoutType === LAYOUT_TYPES.TABS
                    ? tabs.find((t) => t.id === activeTabId)?.content
                    : quadrants[editingQuadrant];
                // Normalize to object { html: "...", style: ... }
                if (q && typeof q.content === "string")
                  return { html: q.content };
                return (q && q.content) || {};
              })()}
              onSave={(updatedContent) => {
                handleSetQuadrantContent(
                  editingQuadrant,
                  CONTENT_TYPES.HTML,
                  updatedContent,
                );
                setEditingQuadrant(null);
              }}
              onCancel={() => setEditingQuadrant(null)}
            />
          )}

        {/* Legacy Editor for other types or if VisualBuilder is not applicable */}
        {editingQuadrant !== null &&
          isEditMode &&
          (layoutType === LAYOUT_TYPES.TABS
            ? tabs.find((t) => t.id === activeTabId)?.content?.type !==
              CONTENT_TYPES.HTML
            : quadrants[editingQuadrant]?.type !== CONTENT_TYPES.HTML) && (
            <QuadrantEditor
              quadrant={
                layoutType === LAYOUT_TYPES.TABS
                  ? tabs.find((t) => t.id === activeTabId)?.content
                  : quadrants[editingQuadrant]
              }
              superAppObjects={superAppObjects}
              workflows={workflows}
              onSave={(contentType, content) =>
                handleSetQuadrantContent(editingQuadrant, contentType, content)
              }
              onCancel={() => setEditingQuadrant(null)}
              onCreateNewObject={() => setShowCreateObjectModal(true)}
              // Vik (Jan 19, 2026) : Handler to switch to Visual Builder. We save the current content as HTML
              // but pass false to closeEditor so the modal stays open (switching the view to VisualBuilder).
              onSwitchToVisualBuilder={(content) => {
                // Save as HTML but keep editor open (which will switch to VisualBuilder view)
                handleSetQuadrantContent(
                  editingQuadrant,
                  CONTENT_TYPES.HTML,
                  content,
                  false,
                );
              }}
            />
          )}

        {showCreateObjectModal && (
          <CreateObjectModal
            onClose={() => {
              setShowCreateObjectModal(false);
              setNewObjectName("");
              setNewObjectKey("");
            }}
            onCreate={handleCreateNewObject}
            name={newObjectName}
            objectKey={newObjectKey}
            onNameChange={setNewObjectName}
            onKeyChange={setNewObjectKey}
          />
        )}

        {editingSuperAppObject && (
          <SuperAppObjectEditor
            object={editingSuperAppObject}
            onClose={() => setEditingSuperAppObject(null)}
            onSave={async (updatedObject) => {
              try {
                const response = await fetchSecure(
                  `/api/superapp/objects/${updatedObject.key}`,
                  {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(updatedObject),
                  },
                );

                if (response.ok) {
                  const saved = await response.json();
                  setSuperAppObjects(
                    superAppObjects.map((obj) =>
                      obj.key === saved.key ? saved : obj,
                    ),
                  );
                  setEditingSuperAppObject(null);
                  alert("SuperAppObject updated successfully!");
                } else {
                  const error = await response.json();
                  alert(`Failed to update: ${error.error || "Unknown error"}`);
                }
              } catch (e) {
                console.error("Failed to update SuperAppObject:", e);
                alert("Failed to update SuperAppObject");
              }
            }}
          />
        )}
      </div>

      <style>{`
        .page-editor-container {
          min-height: 100vh;
          background: #0b1221;
          font-family: 'Inter', sans-serif;
          color: #fff;
          padding-bottom: 100px; /* Vik (Jan 23, 2026) : Increased to make room for floating save button */
        }
        
        /* Vik (Jan 23, 2026) : Floating Save Button Styles - Matches editor-card width and style */
        .floating-save-container {
          position: fixed;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          width: calc(100% - 40px); /* Same as custom-container padding */
          max-width: 1200px; /* Same as custom-container max-width */
          z-index: 999;
        }
        .floating-save-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #1a2035; /* Same as editor-card */
          padding: 16px 24px;
          margin-inline: 15px;
          margin-top: 15px;
          border-radius: 16px; /* Same as editor-card */
          border: 1px solid rgba(255,255,255,0.05); /* Same as editor-card */
          box-shadow: 0 4px 20px rgba(0,0,0,0.2); /* Same as editor-card */
          backdrop-filter: blur(10px);
        }
        .save-status {
          font-size: 0.9rem;
          white-space: nowrap;
        }
        .save-status .status-ready {
          color: #38ef7d;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .save-status .status-ready .status-icon {
          font-size: 14px;
        }
        .save-status .status-pending {
          color: rgba(255, 255, 255, 0.6);
        }
        /* Vik (Jan 23, 2026) : Match the original primary-gradient-btn style */
        .floating-save-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 10px 24px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }
        .floating-save-btn:hover:not(.disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(118, 75, 162, 0.3);
        }
        .floating-save-btn.disabled {
          opacity: 0.6;
          cursor: not-allowed;
          filter: grayscale(1);
        }
        .floating-save-btn .icon {
          font-size: 14px;
        }
        .floating-save-btn .spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .page-editor-header {
          background: rgba(11, 18, 33, 0.95);
          padding: 20px 0;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          margin-bottom: 30px;
          position: sticky;
          top: 0;
          z-index: 100;
          backdrop-filter: blur(10px);
        }
        .custom-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        .gradient-text {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 800;
          font-size: 2rem;
          margin: 0;
        }
        
        /* App Context Badge */
        .app-context-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 10px 20px;
          background: rgba(118, 75, 162, 0.15);
          border: 1px solid rgba(118, 75, 162, 0.3);
          border-radius: 30px;
          color: #a78bfa;
          font-size: 0.95rem;
        }
        .app-context-badge .icon {
          font-size: 16px;
        }
        .app-context-badge strong {
          color: #fff;
          font-weight: 600;
        }
        
        /* Cards */
        .editor-card {
          background: #1a2035;
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.05);
          box-shadow: 0 4px 20px rgba(0,0,0,0.2);
          overflow: hidden;
        }
        .card-header {
          padding: 20px 24px;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          background: rgba(255,255,255,0.02);
        }
        .card-header-flex {
          padding: 20px 24px;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          background: rgba(255,255,255,0.02);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }
        .card-title {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 10px;
          color: #fff;
        }
        .card-body {
          padding: 24px;
        }
        
        /* Inputs */
        .premium-input {
          width: 100%;
          padding: 12px 16px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          color: #fff;
          font-size: 14px;
          transition: all 0.2s;
        }
        .premium-input:focus {
          outline: none;
          border-color: #764ba2;
          background: rgba(255,255,255,0.08);
          box-shadow: 0 0 0 3px rgba(118, 75, 162, 0.1);
        }
        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .input-prefix {
          position: absolute;
          left: 12px;
          color: rgba(255,255,255,0.4);
          font-family: monospace;
        }
        .input-wrapper .premium-input {
          padding-left: 24px;
        }
        .input-group-premium {
          display: flex;
          gap: 12px;
        }
        .input-group-premium input {
          flex: 1;
          padding: 12px 16px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          color: #fff;
        }
        
        /* Buttons */
        .primary-gradient-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 10px 24px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .primary-gradient-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(118, 75, 162, 0.3);
        }
        .primary-gradient-btn.disabled {
          opacity: 0.6;
          cursor: not-allowed;
          filter: grayscale(1);
        }
        .secondary-btn {
          background: rgba(255,255,255,0.1);
          color: #fff;
          border: 1px solid rgba(255,255,255,0.1);
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .secondary-btn:hover {
          background: rgba(255,255,255,0.15);
        }
        
        /* Layout Grid */
        .layout-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 16px;
        }
        .layout-option {
          background: rgba(255,255,255,0.03);
          border: 2px solid rgba(255,255,255,0.05);
          border-radius: 12px;
          padding: 20px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s;
        }
        .layout-option:hover {
          background: rgba(255,255,255,0.06);
          border-color: rgba(255,255,255,0.2);
        }
        .layout-option.selected {
          background: rgba(118, 75, 162, 0.1);
          border-color: #764ba2;
          box-shadow: 0 0 0 2px rgba(118, 75, 162, 0.2);
        }
        .layout-icon {
          font-size: 2rem;
          margin-bottom: 12px;
          color: rgba(255,255,255,0.6);
        }
        .layout-option.selected .layout-icon {
          color: #764ba2;
        }
        
        /* Alerts */
        .info-alert {
          background: rgba(33, 150, 243, 0.1);
          border: 1px solid rgba(33, 150, 243, 0.2);
          border-radius: 12px;
          padding: 20px;
        }
        .alert-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        .alert-header h3 {
          margin: 0;
          font-size: 1rem;
          color: #90caf9;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .close-btn {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.5);
          font-size: 1.5rem;
          cursor: pointer;
          line-height: 1;
        }
        .alert-list {
          margin: 0;
          padding-left: 20px;
          color: rgba(255,255,255,0.8);
          font-size: 0.9rem;
        }
        .alert-list li {
          margin-bottom: 6px;
        }
        
        /* Helpers */
        .helper-text {
          font-size: 0.85rem;
          color: rgba(255,255,255,0.5);
          margin-top: 6px;
          margin-bottom: 0;
        }
        .icon {
          font-size: 1.1em;
        }
        .mb-3 { margin-bottom: 16px; }
        .mb-4 { margin-bottom: 24px; }
        .mt-2 { margin-top: 8px; }
        .mt-3 { margin-top: 16px; }
        .row { display: flex; flex-wrap: wrap; margin: 0 -10px; }
        .col-md-6 { width: 50%; padding: 0 10px; box-sizing: border-box; }
        .form-label { display: block; margin-bottom: 8px; font-weight: 500; font-size: 0.9rem; }
        .text-end { text-align: right; }
        .d-flex { display: flex; }
        .justify-content-end { justify-content: flex-end; }
        .app-id-display { font-family: monospace; color: rgba(255,255,255,0.6); }
        .highlight { color: #fff; background: rgba(255,255,255,0.1); padding: 2px 6px; borderRadius: 4px; }
        .view-mode-banner {
          background: rgba(255,255,255,0.05);
          border: 1px dashed rgba(255,255,255,0.2);
          padding: 16px;
          border-radius: 8px;
          text-align: center;
          color: rgba(255,255,255,0.6);
          margin-bottom: 24px;
        }
      `}</style>
    </div>
  );
}

function Quadrant({ index, quadrant, onClick, style, isEditMode = true }) {
  const hasContent = quadrant?.type && quadrant?.content;

  // Vik (Jan 16, 2026) : Extract container style if available
  const contentStyle =
    hasContent && typeof quadrant.content === "object" && quadrant.content.style
      ? quadrant.content.style
      : {};

  return (
    <div
      onClick={isEditMode ? onClick : undefined}
      style={{
        ...style,
        border: "2px dashed rgba(255,255,255,0.3)",
        borderRadius: "8px",
        padding: "24px",
        cursor: isEditMode ? "pointer" : "default",
        background: hasContent
          ? "rgba(255,255,255,0.05)"
          : "rgba(255,255,255,0.02)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "150px",
        transition: "all 0.2s",
        // Vik (Jan 16, 2026) : Apply user-defined container styles
        ...contentStyle,
        // Ensure border style from content doesn't completely override the dashed border in edit mode unless intended
        border:
          contentStyle.border ||
          (hasContent ? "none" : "2px dashed rgba(255,255,255,0.3)"),
      }}
      onMouseEnter={(e) => {
        if (!contentStyle.background) {
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.5)";
          e.currentTarget.style.background = hasContent
            ? "rgba(255,255,255,0.08)"
            : "rgba(255,255,255,0.05)";
        }
      }}
      onMouseLeave={(e) => {
        if (!contentStyle.background) {
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
          e.currentTarget.style.background = hasContent
            ? "rgba(255,255,255,0.05)"
            : "rgba(255,255,255,0.02)";
        }
      }}
    >
      {hasContent ? (
        <div style={{ textAlign: "center", color: "#fff", width: "100%" }}>
          {quadrant.type === CONTENT_TYPES.HTML ? (
            // Vik (Jan 16, 2026) : Render HTML preview or marker
            <div
              style={{
                textAlign: "left",
                color: contentStyle.color || "inherit",
              }}
              dangerouslySetInnerHTML={{
                __html:
                  typeof quadrant.content === "string"
                    ? quadrant.content
                    : quadrant.content.html,
              }}
            />
          ) : (
            <>
              <p style={{ margin: 0, fontSize: "14px", opacity: 0.8 }}>
                {quadrant.type === CONTENT_TYPES.SUPERAPP_OBJECT
                  ? "SuperAppObject"
                  : quadrant.type === CONTENT_TYPES.WORKFLOW
                    ? "Workflow"
                    : "Content"}
              </p>
              {quadrant.type === CONTENT_TYPES.SUPERAPP_OBJECT &&
                quadrant.content?.key && (
                  <p
                    style={{
                      margin: "4px 0 0 0",
                      fontSize: "12px",
                      opacity: 0.6,
                    }}
                  >
                    {quadrant.content.key}
                  </p>
                )}
              {quadrant.type === CONTENT_TYPES.WORKFLOW &&
                quadrant.content?.workflowName && (
                  <p
                    style={{
                      margin: "4px 0 0 0",
                      fontSize: "12px",
                      opacity: 0.6,
                    }}
                  >
                    {quadrant.content.workflowName}
                  </p>
                )}
            </>
          )}
        </div>
      ) : (
        <div style={{ textAlign: "center", color: "#fff", opacity: 0.6 }}>
          <p style={{ margin: 0 }}>Click to add content</p>
          <p style={{ margin: "4px 0 0 0", fontSize: "12px" }}>
            Quadrant {index + 1}
          </p>
        </div>
      )}
    </div>
  );
}

function QuadrantEditor({
  quadrant,
  superAppObjects,
  onSave,
  onCancel,
  onCreateNewObject,
  workflows = [],
  onSwitchToVisualBuilder,
}) {
  const [contentType, setContentType] = useState(
    quadrant?.type || CONTENT_TYPES.HTML,
  );

  // Vik (Jan 16, 2026) : Initialize HTML content and style state
  const initialHtml =
    quadrant?.type === CONTENT_TYPES.HTML
      ? typeof quadrant.content === "string"
        ? quadrant.content
        : quadrant.content?.html || ""
      : "";

  const initialStyle =
    quadrant?.type === CONTENT_TYPES.HTML &&
    typeof quadrant.content === "object" &&
    quadrant.content?.style
      ? quadrant.content.style
      : {
          backgroundColor: "transparent",
          color: "#ffffff",
          padding: "20px",
          borderRadius: "8px",
          borderWidth: "0px",
          borderColor: "#ffffff",
          borderStyle: "solid",
          boxShadow: "none",
        };

  const [htmlContent, setHtmlContent] = useState(initialHtml);
  const [containerStyle, setContainerStyle] = useState(initialStyle);

  const [selectedObjectKey, setSelectedObjectKey] = useState(
    quadrant?.type === CONTENT_TYPES.SUPERAPP_OBJECT
      ? quadrant.content?.key
      : "",
  );
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(
    quadrant?.type === CONTENT_TYPES.WORKFLOW
      ? quadrant.content?.workflowId
      : "",
  );

  const handleSave = () => {
    if (contentType === CONTENT_TYPES.HTML) {
      // Vik (Jan 16, 2026) : Save HTML content with styles
      onSave(CONTENT_TYPES.HTML, {
        html: htmlContent,
        style: containerStyle,
      });
    } else if (contentType === CONTENT_TYPES.SUPERAPP_OBJECT) {
      const selectedObject = superAppObjects.find(
        (obj) => obj.key === selectedObjectKey,
      );
      if (!selectedObject) {
        alert("Please select a SuperAppObject");
        return;
      }
      onSave(CONTENT_TYPES.SUPERAPP_OBJECT, {
        key: selectedObject.key,
        objectType: "SuperAppObject",
      });
    } else if (contentType === CONTENT_TYPES.WORKFLOW) {
      const selectedWorkflow = workflows.find(
        (w) => w.id === selectedWorkflowId,
      );
      if (!selectedWorkflow) {
        alert("Please select a workflow");
        return;
      }
      onSave(CONTENT_TYPES.WORKFLOW, {
        workflowId: selectedWorkflow.id,
        workflowName: selectedWorkflow.name,
        workflow: selectedWorkflow,
      });
    }
  };

  // Helper for style inputs
  const StyleInput = ({
    label,
    value,
    onChange,
    type = "text",
    options = [],
  }) => (
    <div style={{ marginBottom: "12px" }}>
      <label
        style={{
          color: "rgba(255,255,255,0.7)",
          fontSize: "12px",
          display: "block",
          marginBottom: "4px",
        }}
      >
        {label}
      </label>
      {type === "select" ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: "100%",
            padding: "8px",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "4px",
            color: "#fff",
            fontSize: "14px",
          }}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: "100%",
            padding: "8px",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "4px",
            color: "#fff",
            fontSize: "14px",
          }}
        />
      )}
    </div>
  );

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "#1d1769",
          borderRadius: "8px",
          padding: "24px",
          maxWidth: "900px", // Widen modal for side-by-side editing
          width: "95%",
          maxHeight: "90vh",
          overflow: "auto",
          border: "2px solid rgba(125, 94, 255, 0.5)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <h2 style={{ color: "#e9ecff", marginTop: 0 }}>
          Edit Quadrant Content
        </h2>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{ color: "#e9ecff", display: "block", marginBottom: "8px" }}
          >
            Content Type
          </label>
          <select
            value={contentType}
            onChange={(e) => setContentType(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid rgba(125, 94, 255, 0.5)",
              background: "#090b21",
              color: "#e9ecff",
              fontSize: "16px",
              width: "100%",
            }}
          >
            <option
              value={CONTENT_TYPES.HTML}
              style={{ background: "#090b21", color: "#e9ecff" }}
            >
              HTML & Styling
            </option>
            <option
              value={CONTENT_TYPES.SUPERAPP_OBJECT}
              style={{ background: "#090b21", color: "#e9ecff" }}
            >
              SuperAppObject
            </option>
            <option
              value={CONTENT_TYPES.WORKFLOW}
              style={{ background: "#090b21", color: "#e9ecff" }}
            >
              Workflow
            </option>
          </select>
        </div>

        {contentType === CONTENT_TYPES.HTML && (
          <div style={{ marginBottom: "20px" }}>
            {/* Vik (Jan 19, 2026) : Added button to launch Visual Builder. This provides a better UX for 
                creating complex HTML layouts compared to the basic WYSIWYG editor. */}
            <button
              onClick={() => {
                if (onSwitchToVisualBuilder) {
                  onSwitchToVisualBuilder({
                    html: htmlContent,
                    style: containerStyle,
                  });
                }
              }}
              className="primary-gradient-btn"
              style={{
                width: "100%",
                justifyContent: "center",
                padding: "16px",
              }}
            >
              <FaMagic /> Open Visual Builder
            </button>
            <p
              style={{
                textAlign: "center",
                color: "rgba(255,255,255,0.5)",
                marginTop: "8px",
                fontSize: "14px",
              }}
            >
              Use our advanced drag-and-drop builder to create beautiful
              layouts.
            </p>
          </div>
        )}

        {contentType === CONTENT_TYPES.HTML && (
          <div style={{ display: "flex", gap: "24px", flex: 1, minHeight: 0 }}>
            {/* Left Column: WYSIWYG Editor */}
            <div style={{ flex: 2, display: "flex", flexDirection: "column" }}>
              <label
                style={{
                  color: "#e9ecff",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                HTML Content
              </label>
              <div
                style={{
                  background: "#fff",
                  borderRadius: "4px",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: "400px",
                  // Vik (Jan 16, 2026) : Apply container styles for live preview
                  ...containerStyle,
                  // Ensure we don't break the editor layout
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden", // Clip content to border radius
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={htmlContent}
                  onChange={setHtmlContent}
                  style={{
                    background: "#fff",
                    color: "#000",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, 4, 5, 6, false] }],
                      [{ size: ["small", false, "large", "huge"] }], // Vik (Jan 16, 2026) : Added font size
                      [{ font: [] }],
                      ["bold", "italic", "underline", "strike"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ color: [] }, { background: [] }],
                      [{ align: [] }],
                      ["link", "image"],
                      ["clean"],
                    ],
                  }}
                />
              </div>
            </div>

            {/* Right Column: Container Styling */}
            <div
              style={{
                flex: 1,
                background: "rgba(0,0,0,0.2)",
                padding: "16px",
                borderRadius: "8px",
                overflowY: "auto",
              }}
            >
              <h4
                style={{
                  color: "#fff",
                  marginTop: 0,
                  marginBottom: "16px",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                  paddingBottom: "8px",
                }}
              >
                Container Styling
              </h4>

              <StyleInput
                label="Background Color"
                type="color"
                value={containerStyle.backgroundColor}
                onChange={(v) =>
                  setContainerStyle((s) => ({ ...s, backgroundColor: v }))
                }
              />

              <StyleInput
                label="Text Color"
                type="color"
                value={containerStyle.color}
                onChange={(v) => setContainerStyle((s) => ({ ...s, color: v }))}
              />

              <div style={{ display: "flex", gap: "8px" }}>
                <div style={{ flex: 1 }}>
                  <StyleInput
                    label="Padding"
                    value={containerStyle.padding}
                    onChange={(v) =>
                      setContainerStyle((s) => ({ ...s, padding: v }))
                    }
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <StyleInput
                    label="Radius"
                    value={containerStyle.borderRadius}
                    onChange={(v) =>
                      setContainerStyle((s) => ({ ...s, borderRadius: v }))
                    }
                  />
                </div>
              </div>

              <h5
                style={{
                  color: "rgba(255,255,255,0.8)",
                  margin: "12px 0 8px 0",
                }}
              >
                Border
              </h5>
              <div style={{ display: "flex", gap: "8px" }}>
                <div style={{ flex: 1 }}>
                  <StyleInput
                    label="Width"
                    value={containerStyle.borderWidth}
                    onChange={(v) =>
                      setContainerStyle((s) => ({ ...s, borderWidth: v }))
                    }
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <StyleInput
                    label="Style"
                    type="select"
                    options={[
                      { label: "Solid", value: "solid" },
                      { label: "Dashed", value: "dashed" },
                      { label: "Dotted", value: "dotted" },
                      { label: "None", value: "none" },
                    ]}
                    value={containerStyle.borderStyle}
                    onChange={(v) =>
                      setContainerStyle((s) => ({ ...s, borderStyle: v }))
                    }
                  />
                </div>
              </div>
              <StyleInput
                label="Border Color"
                type="color"
                value={containerStyle.borderColor}
                onChange={(v) =>
                  setContainerStyle((s) => ({ ...s, borderColor: v }))
                }
              />

              <h5
                style={{
                  color: "rgba(255,255,255,0.8)",
                  margin: "12px 0 8px 0",
                }}
              >
                Effects
              </h5>
              <StyleInput
                label="Box Shadow"
                type="select"
                options={[
                  { label: "None", value: "none" },
                  { label: "Subtle", value: "0 2px 4px rgba(0,0,0,0.1)" },
                  { label: "Medium", value: "0 4px 8px rgba(0,0,0,0.2)" },
                  { label: "Strong", value: "0 8px 16px rgba(0,0,0,0.3)" },
                  {
                    label: "Glow (Purple)",
                    value: "0 0 15px rgba(125, 94, 255, 0.5)",
                  },
                ]}
                value={containerStyle.boxShadow}
                onChange={(v) =>
                  setContainerStyle((s) => ({ ...s, boxShadow: v }))
                }
              />
            </div>
          </div>
        )}

        {contentType === CONTENT_TYPES.SUPERAPP_OBJECT && (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                color: "#e9ecff",
                display: "block",
                marginBottom: "8px",
              }}
            >
              Select SuperAppObject
            </label>
            <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
              <select
                value={selectedObjectKey}
                onChange={(e) => setSelectedObjectKey(e.target.value)}
                style={{
                  padding: "8px 12px",
                  borderRadius: "4px",
                  border: "1px solid rgba(125, 94, 255, 0.5)",
                  background: "#090b21",
                  color: "#e9ecff",
                  fontSize: "16px",
                  flex: 1,
                }}
              >
                <option
                  value=""
                  style={{ background: "#090b21", color: "#e9ecff" }}
                >
                  -- Select --
                </option>
                {superAppObjects.map((obj) => (
                  <option
                    key={obj.key}
                    value={obj.key}
                    style={{ background: "#090b21", color: "#e9ecff" }}
                  >
                    {obj.displayName} ({obj.key})
                  </option>
                ))}
              </select>
              <button
                onClick={onCreateNewObject}
                style={{
                  padding: "8px 16px",
                  background: "#2196f3",
                  border: "none",
                  borderRadius: "4px",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Create New
              </button>
            </div>
          </div>
        )}

        {contentType === CONTENT_TYPES.WORKFLOW && (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                color: "#e9ecff",
                display: "block",
                marginBottom: "8px",
              }}
            >
              Select Workflow
            </label>
            <select
              value={selectedWorkflowId}
              onChange={(e) => setSelectedWorkflowId(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "4px",
                border: "1px solid rgba(125, 94, 255, 0.5)",
                background: "#090b21",
                color: "#e9ecff",
                fontSize: "16px",
                width: "100%",
              }}
            >
              <option
                value=""
                style={{ background: "#090b21", color: "#e9ecff" }}
              >
                -- Select Workflow --
              </option>
              {workflows.map((workflow) => (
                <option
                  key={workflow.id}
                  value={workflow.id}
                  style={{ background: "#090b21", color: "#e9ecff" }}
                >
                  {workflow.name}{" "}
                  {workflow.description ? `- ${workflow.description}` : ""}
                </option>
              ))}
            </select>
            {workflows.length === 0 && (
              <p
                style={{ color: "#9ba5c0", fontSize: "12px", marginTop: "8px" }}
              >
                No workflows available. Workflows are loaded from MCP tools.
              </p>
            )}
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: "8px",
            justifyContent: "flex-end",
            marginTop: "auto",
            paddingTop: "16px",
          }}
        >
          <button
            onClick={onCancel}
            style={{
              padding: "8px 24px",
              background: "rgba(255,255,255,0.1)",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "4px",
              color: "#fff",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: "8px 24px",
              background: "#4caf50",
              border: "none",
              borderRadius: "4px",
              color: "#fff",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateObjectModal({
  onClose,
  onCreate,
  name,
  objectKey,
  onNameChange,
  onKeyChange,
}) {
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1001,
      }}
    >
      <div
        style={{
          background: "#1d1769",
          borderRadius: "8px",
          padding: "24px",
          maxWidth: "500px",
          width: "90%",
          border: "2px solid rgba(125, 94, 255, 0.5)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
        }}
      >
        <h2 style={{ color: "#e9ecff", marginTop: 0 }}>
          Create New SuperAppObject
        </h2>

        <div style={{ marginBottom: "16px" }}>
          <label
            // Vik (Jan 14, 2026) : Using CSS variables ensures this modal adapts to the selected theme.
            style={{
              color: "var(--text-color)",
              display: "block",
              marginBottom: "8px",
            }}
          >
            Display Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g., Users Table"
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid rgba(125, 94, 255, 0.5)",
              background: "var(--bg-color)",
              color: "var(--text-color)",
              fontSize: "16px",
              width: "100%",
            }}
          />
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              color: "var(--text-color)",
              display: "block",
              marginBottom: "8px",
            }}
          >
            Key (unique identifier)
          </label>
          <input
            type="text"
            value={objectKey || ""}
            onChange={(e) =>
              onKeyChange(
                e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
              )
            }
            placeholder="e.g., users-table"
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid rgba(125, 94, 255, 0.5)",
              background: "var(--bg-color)",
              color: "var(--text-color)",
              fontSize: "16px",
              width: "100%",
            }}
          />
        </div>

        <div
          style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 24px",
              background: "rgba(125, 94, 255, 0.2)",
              border: "1px solid rgba(125, 94, 255, 0.5)",
              borderRadius: "4px",
              color: "var(--text-color)",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onCreate}
            style={{
              padding: "8px 24px",
              background:
                "linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%)",
              border: "none",
              borderRadius: "4px",
              color: "#fff",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function SuperAppObjectEditor({ object, onClose, onSave }) {
  const [displayName, setDisplayName] = useState(object?.displayName || "");
  const [configJson, setConfigJson] = useState(
    JSON.stringify(object?.config || {}, null, 2),
  );
  const [configError, setConfigError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!displayName.trim()) {
      alert("Please enter a display name");
      return;
    }

    let parsedConfig;
    try {
      parsedConfig = JSON.parse(configJson);
      setConfigError("");
    } catch (e) {
      setConfigError(`Invalid JSON: ${e.message}`);
      return;
    }

    setSaving(true);
    try {
      await onSave({
        ...object,
        displayName: displayName.trim(),
        config: parsedConfig,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1002,
      }}
    >
      <div
        style={{
          background: "var(--surface-color)",
          borderRadius: "8px",
          padding: "24px",
          maxWidth: "900px",
          width: "90%",
          maxHeight: "90vh",
          overflow: "auto",
          border: "2px solid rgba(125, 94, 255, 0.5)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
        }}
      >
        <h2 style={{ color: "var(--text-color)", marginTop: 0 }}>
          Edit SuperAppObject
        </h2>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              color: "var(--text-color)",
              display: "block",
              marginBottom: "8px",
            }}
          >
            Display Name
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g., Users Table"
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid rgba(125, 94, 255, 0.5)",
              background: "var(--bg-color)",
              color: "var(--text-color)",
              fontSize: "16px",
              width: "100%",
            }}
          />
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              color: "var(--text-color)",
              display: "block",
              marginBottom: "8px",
            }}
          >
            Key (unique identifier)
            <span style={{ fontSize: "12px", opacity: 0.7, marginLeft: "8px" }}>
              (read-only)
            </span>
          </label>
          <input
            type="text"
            value={object?.key || ""}
            disabled
            style={{
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid rgba(125, 94, 255, 0.3)",
              background: "var(--bg-color)",
              color: "#9ba5c0",
              fontSize: "16px",
              width: "100%",
            }}
          />
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              color: "var(--text-color)",
              display: "block",
              marginBottom: "8px",
            }}
          >
            Configuration (JSON)
            <span style={{ fontSize: "12px", opacity: 0.7, marginLeft: "8px" }}>
              Edit the table structure, fields, collection, etc.
            </span>
          </label>
          {configError && (
            <div
              style={{
                padding: "8px",
                background: "rgba(255,0,0,0.2)",
                border: "1px solid rgba(255,0,0,0.5)",
                borderRadius: "4px",
                color: "#ff6b6b",
                marginBottom: "8px",
                fontSize: "14px",
              }}
            >
              {configError}
            </div>
          )}
          <textarea
            value={configJson}
            onChange={(e) => {
              setConfigJson(e.target.value);
              setConfigError("");
            }}
            style={{
              padding: "12px",
              borderRadius: "4px",
              border: "1px solid rgba(125, 94, 255, 0.5)",
              background: "var(--bg-color)",
              color: "var(--text-color)",
              fontSize: "14px",
              width: "100%",
              minHeight: "400px",
              fontFamily: "monospace",
              lineHeight: "1.5",
            }}
            placeholder='{\n  "table": [...],\n  "fields": [...],\n  "collection": "...",\n  ...\n}'
          />
          <div style={{ marginTop: "8px", fontSize: "12px", color: "#9ba5c0" }}>
            <strong>Common properties:</strong> table (array of columns), fields
            (array of form fields), collection (string), tableName (string),
            otherTableOptions (object with paginationPerPage), filterField
            (string), defaultSort (object with selector and asc)
          </div>
        </div>

        <div
          style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}
        >
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: "8px 24px",
              background: "rgba(125, 94, 255, 0.2)",
              border: "1px solid rgba(125, 94, 255, 0.5)",
              borderRadius: "4px",
              color: "var(--text-color)",
              cursor: saving ? "not-allowed" : "pointer",
              fontSize: "16px",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "8px 24px",
              background: saving
                ? "#666"
                : "linear-gradient(217.56deg, #785eff -39.41%, #4d35ff 99.53%)",
              border: "none",
              borderRadius: "4px",
              color: "#fff",
              cursor: saving ? "not-allowed" : "pointer",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
